/**
 * Global data-scope pill in the top bar (SYR-68).
 *
 * Summarises the active data window ("Last 180 days · All cohorts") and opens
 * the ScopeDrawer. This is the single, persistent control for the data that
 * governs every destination.
 */

import React from 'react';
import { Grid } from 'antd';
import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useMarketerScope, type PeriodType } from '../scope/MarketerScopeContext';
import { THEME } from '../config/theme';

export const PERIOD_SUMMARY: Record<PeriodType, { id: string; def: string }> = {
  last30: { id: 'marketerView.period.short.last30', def: 'Last 30 days' },
  last60: { id: 'marketerView.period.short.last60', def: 'Last 60 days' },
  last90: { id: 'marketerView.period.short.last90', def: 'Last 90 days' },
  last120: { id: 'marketerView.period.short.last120', def: 'Last 120 days' },
  last180: { id: 'marketerView.period.short.last180', def: 'Last 180 days' },
  last365: { id: 'marketerView.period.short.last365', def: 'Last 365 days' },
  allTime: { id: 'marketerView.period.short.allTime', def: 'All time' },
  custom: { id: 'marketerView.period.short.custom', def: 'Custom range' },
};

function formatCalculatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

export const ScopePill: React.FunctionComponent<{ onOpen: () => void }> = ({
  onOpen,
}) => {
  const intl = useIntl();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { scope, totalResponses, calculatedAt } = useMarketerScope();

  const periodLabel = intl.formatMessage({
    id: PERIOD_SUMMARY[scope.periodType].id,
    defaultMessage: PERIOD_SUMMARY[scope.periodType].def,
  });

  const cohortLabel =
    scope.cohortIds.length === 0
      ? intl.formatMessage({
          id: 'marketerView.scope.allCohorts',
          defaultMessage: 'All cohorts',
        })
      : intl.formatMessage(
          {
            id: 'marketerView.scope.nCohorts',
            defaultMessage: '{n} cohorts',
          },
          { n: scope.cohortIds.length },
        );

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 14px',
        borderRadius: 999,
        border: `1px solid ${THEME.border}`,
        background: THEME.bgChip,
        color: THEME.textBase,
        fontSize: 14,
        cursor: 'pointer',
        maxWidth: '100%',
      }}
    >
      <CalendarOutlined style={{ color: THEME.primaryDark, flexShrink: 0 }} />
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        <strong style={{ fontWeight: 700 }}>{periodLabel}</strong>
        {!isMobile && (
          <span style={{ color: THEME.textSecondary, fontWeight: 500 }}>
            {' · '}
            {cohortLabel}
            {scope.countryCodes.length > 0
              ? ` · ${intl.formatMessage(
                  {
                    id: 'marketerView.scope.nCountries',
                    defaultMessage: '{n} countries',
                  },
                  { n: scope.countryCodes.length },
                )}`
              : ''}
            {totalResponses != null
              ? ` · ${intl.formatMessage(
                  {
                    id: 'marketerView.scope.nResponses',
                    defaultMessage: '{n} responses',
                  },
                  { n: totalResponses },
                )}`
              : ''}
            {calculatedAt
              ? ` · ${intl.formatMessage({
                  id: 'marketerView.scope.calculated',
                  defaultMessage: 'Calculated',
                })} ${formatCalculatedAt(calculatedAt)}`
              : ''}
          </span>
        )}
      </span>
      <DownOutlined style={{ fontSize: 11, color: THEME.textMuted, flexShrink: 0 }} />
    </button>
  );
};
